import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Field, FieldProps, Formik } from 'formik';
import i18n from 'i18next';
import { cloneDeep } from 'lodash';
import * as Yup from 'yup';
import { ValidationError } from 'yup';

import { Notification, NotificationBody } from '@adeccoux/tag-ds';
import { Header } from '@components/app/Header/Header';
import AuthLayout from '@components/shared/AuthLayout/AuthLayout';
import { Loader } from '@components/shared/Loader/Loader';
import { useInjection } from '@context/inversify-context-provider';
import { useOverlayLoader } from '@context/OverlayLoaderContext';
import {
  AuthHelper,
  AzureB2CRedirectType,
  getRegistrationStepInfo,
  InvalidCodeVerifierError,
  NavigationError,
} from '@helpers/authHelper';
import {
  getClientOrganizationId,
  getSelectedConfigurationId,
  getTenantCountryCode,
  getTenantDefaultLanguage,
} from '@helpers/tenant.helper';
import { isCurrentLanguageGreek } from '@helpers/utils';
import useTelemetry from '@hooks/useTelemetry';
import { TYPES } from '@libs/InversifyType';
import { EventType, TargetName, TelemetryActor } from '@models/telemetry.model';
import { CANDIDATE_EMAIL_STATUS_IMPORTED, CANDIDATE_EMAIL_STATUS_NOT_FOUND } from '@models/user.model';
import { Alert, Button, TextField } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { useQuery } from '@tanstack/react-query';

import { AamBackendApi } from '../../../libs/aamBackendApi';
import { CustomLogo } from '../controls/CustomLogo';
import { OOFormControl } from '../controls/FormControl';
import { OOControlModel } from '../models/ControlModel';
import { OOPageModel } from '../models/FormModel';
import { OOFlowWrapper } from '../wrappers/FlowWrapper';

import styles from '../../oneOnboardingAdmin/pages/AdminImportUsers/AdminImportUsers.module.scss';

export class CandidatePreRegisterError extends Error {
  originalError: Error | undefined;
  constructor(public readonly message: string, originalError?: Error) {
    super(message);
    this.name = 'CandidatePreRegisterError';
    this.originalError = originalError;
    if (originalError) this.stack = `${this.stack} \n ${this.originalError?.stack}`;
  }
  logError() {
    console.trace(this);
  }
}

export const OOPreRegisterPage = () => {
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const b2c = useSelector((state: RootState) => state.featureConfiguration.b2c);
  const userId = useSelector((state: RootState) => state.authApp.userId);
  const appSettings = useSelector((state: RootState) => state.featureConfiguration.appSettings);
  const tenant = useSelector((state: RootState) => state.tenant);
  const authHelper = useInjection<AuthHelper>(TYPES.AuthHelper);
  const tokenApp = useSelector((state: RootState) => state.authApp.idToken);
  const authMethodApp = useSelector((state: RootState) => state.authApp.authMethod);
  const history = useHistory();
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const { t } = useTranslation(['entry', 'candidate', 'candidate_recruiter']);
  const userEmail = history?.location?.state?.email || '';

  const oneOnboarding = useSelector((state: RootState) => state.oneOnboarding);
  const [fw, setFw] = useState<OOFlowWrapper | null>(null);
  const [addData, setAddData] = useState<any>(null);
  const [logo, setLogo] = useState<string>('');
  const [consentPage, setConsentPage] = useState<OOPageModel | null>(null);
  const { createTelemetryUnauthenticated } = useTelemetry();
  const { showLoader, hideLoader } = useOverlayLoader();
  const [error, setError] = useState<CandidatePreRegisterError | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>(null);

  const clientOrganizationId = useMemo(() => {
    if (oneOnboarding.clientOrganizationId) {
      return oneOnboarding.clientOrganizationId;
    }
    return getClientOrganizationId() || '';
  }, [oneOnboarding.clientOrganizationId]);
  const configurationId = useMemo(() => {
    if (oneOnboarding.selectedConfigurationId) {
      return oneOnboarding.selectedConfigurationId;
    }
    return getSelectedConfigurationId() || '';
  }, [oneOnboarding.selectedConfigurationId]);

  const applicationId = useMemo(() => {
    if (oneOnboarding.applicationId) {
      return oneOnboarding.applicationId;
    }
  }, [oneOnboarding.applicationId]);

  const validationSchema = useMemo(() => {
    if (!fw) return null;

    return Yup.object().shape({
      email: Yup.string()
        .email()
        .required()
        .test('account-test', 'account is not valid', async (value) => {
          if (!value) {
            return false;
          }

          const emailStatus = await aamBackendApi.getCandidateEmailStatus(tenantId, value.toLowerCase());
          if (emailStatus === CANDIDATE_EMAIL_STATUS_NOT_FOUND) {
            return new ValidationError('account unavailable', value, 'email');
          } else if (emailStatus === CANDIDATE_EMAIL_STATUS_IMPORTED) {
            return true;
          }
          return true;
        }),
      ...consentPage?.getValidationObject(fw, t),
    });
  }, [fw, consentPage, t, aamBackendApi, tenantId]);

  const getCurrentLng = () => i18n.language || window.localStorage.i18nextLng || getTenantDefaultLanguage(tenantId);
  const b2cSignUp = async (values: any) => {
    setSubmitting(true);
    showLoader(t('candidate_recruiter:GENERAL.GENERIC.Submitting'));
    let tokenHint = undefined;
    try {
      tokenHint = await aamBackendApi.getTokenHint(tenantId, values.email, clientOrganizationId);
      await createTelemetryUnauthenticated({
        eventType: EventType.BUTTON_CLICKED,
        targetName: TargetName.CandidateRegistrationContinue,
        applicationId: applicationId || '',
        clientOrganizationId: clientOrganizationId,
        configurationId: configurationId,
        email: userEmail,
        tenantId: tenantId,
        actor: TelemetryActor.CANDIDATE,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error?.data?.message || err.data?.message || err.message;
      if (errorMessage.toLowerCase().includes('user already exists')) {
        values.from = AzureB2CRedirectType.signIn;
        values.isEmailInputDisabled = false;
        try {
          const clonedIdpOptions = cloneDeep(values?.idpOptions);
          const signInUrl = await authHelper.getAuthorizeUrlSignIn(b2c.signIn, history.location, values.email, {
            ...clonedIdpOptions,
            ...values,
            idpOptions: clonedIdpOptions,
          });
          authHelper.b2cNavigation(
            b2c.signIn,
            signInUrl + '&ui_locales=' + getCurrentLng() + '&country=' + getTenantCountryCode(tenantId),
            history,
            appSettings.useExternalBrowser,
          );
        } catch (err) {
          const errorWrapper = new CandidatePreRegisterError('UserExistsRedirectionError', err);
          errorWrapper.logError();
        }
        hideLoader();
        return;
      }
      const tokenError = new CandidatePreRegisterError(
        `There was a problem retreiving token hint for user ${values.email}: ${err.data?.message || err.message}`,
      );
      tokenError.logError();
    }
    let signUpUrl;
    try {
      const clonedIdpOptions = cloneDeep(values?.idpOptions);
      signUpUrl = await authHelper.getAuthorizeUrlSignup(b2c.signUp, history.location, values.email, {
        ...clonedIdpOptions,
        ...values,
        idpOptions: clonedIdpOptions,
      });
    } catch (err) {
      const errorWrapper = new CandidatePreRegisterError('AuthError', err);
      errorWrapper.logError();
      if (!(err instanceof InvalidCodeVerifierError)) throw errorWrapper;
      setError(errorWrapper);
    }
    const tenantCountryCode = getTenantCountryCode(tenantId);
    const currentLocale = getCurrentLng();
    let authUrl = `${signUpUrl}&ui_locales=${currentLocale}&country=${tenantCountryCode}&id_token_hint=${tokenHint}`;
    if (!tokenHint) {
      authUrl = `${signUpUrl}&ui_locales=${currentLocale}&country=${tenantCountryCode}&login_hint=${
        values.email ?? ''
      }`.replace('userjourney=signup', 'userjourney=walkin');
    }
    try {
      authHelper.b2cNavigation(b2c.signUp, authUrl, history, appSettings.useExternalBrowser);
    } catch (err) {
      const errorWrapper = new CandidatePreRegisterError(
        `NavigationError for url:${authUrl} with params ${JSON.stringify({
          issuer: b2c?.signUp?.issuer,
          clientId: b2c?.signUp?.clientId,
          flowUrl: b2c?.signUp?.flowUrl,
        })}`,
        err,
      );
      errorWrapper.logError();
      if (!(err instanceof NavigationError)) throw errorWrapper;
      setError(errorWrapper);
    }
    hideLoader();
  };

  const { isInitialLoading: flowLoading = true, data = null, isError } = useQuery({
    queryKey: ['flowData', clientOrganizationId, configurationId],
    queryFn: () => aamBackendApi.fetchSelectedConfiguration(clientOrganizationId, configurationId),
    enabled: !!clientOrganizationId,
  });

  useEffect(() => {
    if (isError) {
      history.push('/oo/preregister', { email: userEmail });
    }
  }, [isError, history, userEmail]);

  useEffect(() => {
    if (data) {
      const flow = OOFlowWrapper.create(data.flow);
      const registrationStep = flow.steps.find((itm) => itm.name.toLowerCase().includes('regist'));
      const idpOptions =
        registrationStep?.elements.length &&
        registrationStep.elements.filter((element) => element.name === 'IDP_OPTIONS')[0]?.additionalData;

      if (
        registrationStep &&
        userEmail &&
        registrationStep.pages.length > 0 &&
        registrationStep.pages.every((i) => i.skip)
      ) {
        b2cSignUp({ email: userEmail, idpOptions });
      } else {
        setAddData(idpOptions);
        setLogo(data.logo || '');
        setFw(flow);
        const [regStepName, regPageName] =
          flow && flow.steps ? getRegistrationStepInfo({ ...flow, flow: flow.steps }) : [];
        const consentStep = flow && flow.steps ? flow.getStep(regStepName) : null;
        const page = consentStep?.pages?.find((itm) => itm.name === regPageName);
        if (page) {
          setConsentPage(page);
        }
      }
    } else if (data != null && !flowLoading) {
      history.push('/oo');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, flowLoading]);

  useEffect(() => {
    createTelemetryUnauthenticated({
      eventType: EventType.PAGE_LOADED,
      targetName: TargetName.CandidateRegistration,
      applicationId: applicationId,
      clientOrganizationId: clientOrganizationId,
      configurationId: configurationId,
      email: userEmail,
      tenantId: tenantId,
      actor: TelemetryActor.CANDIDATE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const consentPageInitialValues = useMemo(() => {
    return fw && consentPage ? consentPage.getInitialValues(fw) : null;
  }, [fw, consentPage]);

  useEffect(() => {
    if (consentPageInitialValues) {
      setInitialValues({
        email: userEmail,
        ...consentPageInitialValues,
      });
    }
  }, [consentPageInitialValues, userEmail]);

  if (!initialValues) return null; // or loader

  if (flowLoading) {
    return <Loader />;
  }

  if (userId) {
    history.push('/oo/flow');
    return <></>;
  }

  const b2cSignIn = async () => {
    const signInUrl = await authHelper.getAuthorizeUrlSignIn(b2c.signIn, history.location, undefined, addData);
    const authUrl = userEmail
      ? `${signInUrl}&ui_locales=${getCurrentLng()}&login_hint=${encodeURIComponent(
          userEmail,
        )}&country=${getTenantCountryCode(tenantId)}`
      : `${signInUrl}&ui_locales=${getCurrentLng()}&country=${getTenantCountryCode(tenantId)}`;
    authHelper.b2cNavigation(b2c.signIn, authUrl, history, appSettings.useExternalBrowser);
  };

  const textErrors = (meta: any) => ({
    error: meta && meta.touched && Boolean(meta.error),
    helperText: meta && meta.touched && meta.error,
  });

  const isAU = tenant?.tenantId === 208;

  const PreRegisterAU = () => {
    return (
      <AuthLayout>
        <div className="preRegister">
          <div style={{ marginBottom: '24px' }}>
            <Notification
              icon={<span className="material-icons">info_outline</span>}
              info
              show={true}
              className="notificationPreRegister"
            >
              <NotificationBody>{t('candidate:GENERAL.REGISTRATION.consentInfoText')}</NotificationBody>
            </Notification>
          </div>
          <div className="innerContainer">
            <h2 style={{ textAlign: 'center' }}>{t('candidate:GENERAL.REGISTRATION.registerAndCompleteText')}</h2>
            <div className="row">
              <p>{t('candidate:GENERAL.CONTACT_US.email')}</p>
            </div>

            <Formik
              validateOnMount={true}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                await b2cSignUp({ ...values, idpOptions: addData });
              }}
            >
              {(props) => (
                <form onSubmit={props.handleSubmit} style={{ display: 'grid', gap: 16 }}>
                  <Field name="email">
                    {({ field, meta }: FieldProps<string, any>) => (
                      <TextField
                        {...field}
                        {...textErrors(meta)}
                        placeholder={t('candidate:GENERAL.REGISTRATION.emailFieldPlaceholder')}
                        disabled={!!userEmail}
                      />
                    )}
                  </Field>
                  {error || submitting ? (
                    <></>
                  ) : (
                    <span className="tertiaryHeader">
                      {t('candidate:GENERAL.REGISTRATION.doYouHaveAProfile')}{' '}
                      <span onClick={b2cSignIn} className="secondaryHeader">
                        {t('candidate:GENERAL.REGISTRATION.logInHere')}
                      </span>
                    </span>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color={error ? 'error' : 'primary'}
                    disabled={!props.isValid}
                  >
                    {error ? <></> : t('candidate_recruiter:GENERAL.GENERIC.submit')}
                  </Button>
                  {error || submitting ? (
                    <></>
                  ) : (
                    consentPage?.controls
                      .filter((control) => control.isVisibleCandidate)
                      .map((control: OOControlModel) => <OOFormControl control={control} key={control.name} />)
                  )}
                  {error ? (
                    <Notification className={styles.notification} error show={true}>
                      <NotificationBody>
                        {error.name + ': ' + error?.message ||
                          JSON.stringify({
                            name: error?.name,
                            message: error?.message,
                            stack: error?.stack,
                          })}
                      </NotificationBody>
                    </Notification>
                  ) : (
                    <></>
                  )}
                </form>
              )}
            </Formik>
          </div>
        </div>
      </AuthLayout>
    );
  };

  const PreRegisterDefault = () => {
    return (
      <div className="preRegister">
        <Header
          isAuthenticated={Boolean(tokenApp) && authMethodApp === 'b2c'}
          {...{ CustomLogo: logo ? <CustomLogo src={logo} /> : null }}
          isOnPreRegisterPage={true}
        />
        <div className="innerContainer">
          <h1 className="primaryHeader">{t('candidate:GENERAL.REGISTRATION.registerAndCompleteText')}</h1>

          <div className="row">
            <p>{t('candidate:GENERAL.CONTACT_US.email')}</p>
          </div>

          <Formik
            validateOnMount={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              await b2cSignUp({ ...values, idpOptions: addData });
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit} style={{ display: 'grid', gap: 16 }}>
                <Field name="email">
                  {({ field, meta }: FieldProps<string, any>) => (
                    <TextField
                      {...field}
                      {...textErrors(meta)}
                      placeholder={t('candidate:GENERAL.REGISTRATION.emailFieldPlaceholder')}
                      disabled={!!userEmail}
                    />
                  )}
                </Field>
                {error || submitting ? (
                  <></>
                ) : (
                  <span className="tertiaryHeader">
                    {t('candidate:GENERAL.REGISTRATION.doYouHaveAProfile')}{' '}
                    <span
                      onClick={b2cSignIn}
                      className="secondaryHeader"
                      style={{ textTransform: `${isCurrentLanguageGreek() ? 'none' : 'uppercase'}` }}
                    >
                      {t('candidate:GENERAL.REGISTRATION.logInHere')}
                    </span>
                  </span>
                )}
                {error || submitting ? (
                  <></>
                ) : (
                  consentPage?.controls
                    .filter((control) => control.isVisibleCandidate)
                    .map((control: OOControlModel) => <OOFormControl control={control} key={control.name} />)
                )}
                <Button type="submit" variant="contained" color={error ? 'error' : 'primary'} disabled={!props.isValid}>
                  {error ? <></> : t('candidate_recruiter:GENERAL.GENERIC.submit')}
                </Button>
                {error ? (
                  <Alert icon={<span className="material-icons">error_outline</span>} severity="error">
                    <Notification className={styles.notification} error show={true}>
                      <NotificationBody>
                        {error.name + ': ' + error?.message ||
                          JSON.stringify({
                            name: error?.name,
                            message: error?.message,
                            stack: error?.stack,
                          })}
                      </NotificationBody>
                    </Notification>
                  </Alert>
                ) : (
                  <></>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    );
  };

  return isAU ? PreRegisterAU() : PreRegisterDefault();
};
